import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AcademicCapIcon,
  PlayIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  TrashIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { Progress, Popconfirm, message } from "antd";
import axios from "axios";
import Navbar from "../../Components/Navbar/Navbar";
import CoursesPage from "../../Components/Courses/CoursesPage";
import { getUser } from "../../services/Authservice";
import getVideos from "../../apis/getVideos";
import handleUpdateCourse from "../../apis/updateCourse";

export default function Dashboard() {
  const user = getUser();
  const uid = user?.emails[0].value;
  const [courses, setCourses] = useState([]);
  const [status, setStatus] = useState("loading");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setStatus("loading");
    const load = async () => {
      let playlists;
      try {
        const { data } = await axios.get(
          `/api/user/allcourses/${encodeURIComponent(uid)}`,
          { timeout: 20000 },
        );
        playlists = data.flat();
      } catch (error) {
        if (error.response?.status === 404) playlists = [];
        else throw error;
      }
      const data = await Promise.all(
        playlists.map(async (playlist) => {
          const { data } = await axios.get(
            `/api/user/getvideos/${encodeURIComponent(uid)}/${playlist.playlistID}`,
            { timeout: 20000 },
          );
          return { ...playlist, videos: data.videos };
        }),
      );
      if (active) {
        setCourses(data);
        setStatus("ready");
      }
    };
    load().catch(() => {
      if (active) setStatus("error");
    });
    return () => {
      active = false;
    };
  }, [uid, attempt]);
  const videos = courses.flatMap((course) => course.videos);
  const watched = videos.filter((video) => video.watched).length;
  const completed = courses.filter(
    (course) =>
      course.videos.length && course.videos.every((video) => video.watched),
  ).length;
  const deleteCourse = async (playlistID) => {
    try {
      await axios.delete(
        `/api/user/deletevideos/${encodeURIComponent(uid)}/${playlistID}`,
      );
      setCourses((previous) =>
        previous.filter((course) => course.playlistID !== playlistID),
      );
      message.success("Course removed.");
    } catch {
      message.error("We couldn’t remove this course. Please try again.");
    }
  };
  const syncCourse = async (course) => {
    try {
      const items = await getVideos(course.playlistID);
      const known = new Set(course.videos.map((video) => video.videoId));
      const added = items.filter(
        (item) => !known.has(item.snippet.resourceId.videoId),
      );
      if (!added.length) {
        message.info("Your course is up to date.");
        return;
      }
      await handleUpdateCourse(course.playlistID, uid, added);
      setAttempt((value) => value + 1);
    } catch {
      message.error("We couldn’t update the course. Please try again.");
    }
  };

  return (
    <>
      <Navbar />
      <main id="main-content">
        <div className="page-width page-main">
          <div className="page-heading page-heading-row">
            <div>
              <p className="eyebrow">MAKE A LITTLE PROGRESS TODAY</p>
              <h1>
                Welcome back, {user?.name?.givenName || "learner"}
                <span style={{ color: "var(--yellow)" }}>.</span>
              </h1>
              <p>Your next step is right where you left it.</p>
            </div>
            <Link className="button button-secondary" to="/">
              Explore courses <ArrowRightIcon />
            </Link>
          </div>
          <div className="stats-grid">
            {[
              [AcademicCapIcon, courses.length, "Saved courses"],
              [PlayIcon, watched, "Lessons completed"],
              [CheckCircleIcon, completed, "Courses completed"],
            ].map(([Icon, value, label]) => (
              <div className="surface stat-card" key={label}>
                <span className="stat-icon">
                  <Icon />
                </span>
                <div>
                  <strong>{status === "ready" ? value : "—"}</strong>
                  <p>{label}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="section-heading">
            <div>
              <h2>Your learning space</h2>
              <p>
                {status === "ready" && videos.length
                  ? `${Math.round((watched / videos.length) * 100)}% of your saved lessons completed. Keep going.`
                  : "Small steps today. New possibilities tomorrow."}
              </p>
            </div>
          </div>
          {status === "loading" ? (
            <div className="empty-state surface" role="status">
              <span className="loading-ring" />
              <p>Finding your place…</p>
            </div>
          ) : status === "error" ? (
            <div className="empty-state surface" role="alert">
              <h3>We couldn’t load your learning.</h3>
              <p>Your progress hasn’t changed. Please try again.</p>
              <button
                className="button button-secondary"
                onClick={() => setAttempt(attempt + 1)}
              >
                Try again
              </button>
            </div>
          ) : !courses.length ? (
            <div className="empty-state surface">
              <AcademicCapIcon />
              <h3>A fresh start. Endless possibilities.</h3>
              <p>
                Save your first course and your progress will live right here.
              </p>
              <Link className="button button-primary" to="/">
                Find your first course <ArrowRightIcon />
              </Link>
            </div>
          ) : (
            <div className="course-grid">
              {courses.map((course) => {
                const count = course.videos.filter(
                  (video) => video.watched,
                ).length;
                const progress = course.videos.length
                  ? Math.round((count / course.videos.length) * 100)
                  : 0;
                return (
                  <article
                    className="course-card enrolled-card"
                    key={course.playlistID}
                  >
                    <span className="enrolled-label">
                      {progress === 100
                        ? "COURSE COMPLETE"
                        : "YOUR NEXT CHAPTER"}
                    </span>
                    <h3>{course.title}</h3>
                    <Progress percent={progress} strokeColor="#facc15" />
                    <p className="course-description">
                      {count} of {course.videos.length} lessons completed
                    </p>
                    <div className="course-actions">
                      <Link
                        to="/video-player"
                        state={{
                          playlistID: course.playlistID,
                          tracking: true,
                        }}
                      >
                        {progress === 100
                          ? "Revisit course"
                          : "Continue learning"}
                        <ArrowRightIcon />
                      </Link>
                      <button
                        onClick={() => syncCourse(course)}
                        aria-label={`Check for new lessons in ${course.title}`}
                      >
                        <ArrowPathIcon />
                      </button>
                      <Popconfirm
                        title="Remove this course?"
                        description="Your progress for this course will be deleted."
                        onConfirm={() => deleteCourse(course.playlistID)}
                        okText="Remove"
                      >
                        <button aria-label={`Remove ${course.title}`}>
                          <TrashIcon />
                        </button>
                      </Popconfirm>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
        <CoursesPage />
      </main>
    </>
  );
}
